import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoSheetPanel, Row, Button, useOverlay } from '@fs/zion-ui'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import SharedEventContent from './SharedEventContent'
import useUnsavedChangesOverlay from '../../../../hooks/useUnsavedChangesOverlay'
import UnsavedChangesOverlay from '../shared/UnsavedChangesOverlay'

/**
 * Callback function for the onSave event data.
 *
 * @callback onSaveCallback
 * @param {Object[]} dispatches - An array of dispatches.
 * @param {Object[]} formValues - The form values object from the event panel.
 */

/**
 * Callback function for the View In Context button.
 *
 * @callback viewInContextHandler
 * @param {Number} pageNumber - image index + 1
 */

/**
 * Component for showing the attachments of an event
 * @param {Object} selectedPerson - the selected person, who is already attached to this event
 * @param {Object} event - the event to display attachments for
 * @param {Boolean} shouldSaveToSls - whether to save the changes to SLS or just pass the dispatches to onSave
 * @param {Function} onCancel - Called when the user cancels out of the panel
 * @param {Function} onSave - Called with dispatches either after or instead of save
 * @param {Object} defaultFormState - Optional default form state for the panel. When shouldSaveToSls is false, this helps preserve state after closing and reopening..
 * @param {Function} viewInContextHandler - Called with pageNumber. Should display the corresponding image.
 * @param {String} addedEventId - if this event is not yet created, this is the id it will have
 * @param {String} appName - name of the app that is using this component
 * @returns {Element}
 */
export default function SharedEventPanel({
  selectedPerson,
  event,
  shouldSaveToSls = false,
  onSave = () => {},
  onCancel = () => {},
  defaultFormState,
  viewInContextHandler,
  addedEventId,
  appName,
}) {
  if (!appName) throw new Error('SharedEventPanel: appName is required parameter')

  const [t] = useTranslation()
  const { closeOverlay, openOverlay, sharedEventOverlay } = useOverlayContext()
  const [saving, setSaving] = useState(false)
  const saveChangesOverlay = useOverlay()
  const [showSaveChangesOverlay, setShowSaveChangesOverlay] = useState(false)
  const updateButtonRef = useRef(null)

  useEffect(() => {
    openOverlay('sharedEvent')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Open overlay as soon as component mounts

  const handleCancel = () => {
    if (showSaveChangesOverlay) {
      saveChangesOverlay.open()
    } else {
      closeOverlay('sharedEvent')
      onCancel()
    }
  }

  // The back arrow on panel 2 was clicked
  const userClosedPanel = () => {
    handleCancel()
  }

  const onDirtyChange = useCallback((isDirty) => {
    setShowSaveChangesOverlay(isDirty)
  }, [])

  useUnsavedChangesOverlay({
    overlay: saveChangesOverlay,
    panelId: 'sharedEvent',
    condition: showSaveChangesOverlay,
  })

  return (
    <InfoSheetPanel
      title={t('shared-event.view-name', 'View Name')}
      subtitle={t('shared-event.view-subtitle', 'Manage Linked Names on Event')}
      headingColor="red"
      {...{ ...sharedEventOverlay, close: userClosedPanel }}
      footer={
        <Row alignX="end" gutters="md">
          <Button
            data-testid="shared-event-back-button"
            linkName="Shared Event Back Button"
            emphasis="low"
            disabled={saving}
            onClick={handleCancel}
          >
            {t('shared-event.cancel.btn', 'Cancel')}
          </Button>
          <Button
            data-testid="shared-event-forward-button"
            linkName="Shared Event Forward Button"
            emphasis="high"
            disabled={saving}
            form="shared-event"
            type="submit"
            ref={updateButtonRef}
          >
            {t('shared-event.update.btn', 'Update')}
          </Button>
        </Row>
      }
    >
      <SharedEventContent
        selectedPerson={selectedPerson}
        event={event}
        shouldSaveToSls={shouldSaveToSls}
        onSave={onSave}
        viewInContextHandler={viewInContextHandler}
        defaultFormState={defaultFormState}
        addedEventId={addedEventId}
        appName={appName}
        setSaving={setSaving}
        onDirtyChange={onDirtyChange}
      />
      <UnsavedChangesOverlay
        disableSave={saving}
        overlay={saveChangesOverlay}
        onDiscard={() => {
          saveChangesOverlay.close()
          onCancel()
        }}
        onSave={() => {
          saveChangesOverlay.close()
          updateButtonRef.current.click()
        }}
        formId="shared-event"
      />
    </InfoSheetPanel>
  )
}
